import { Add } from '@bufferapp/ui/Icon'
import styled from 'styled-components'

import { Tooltip } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useIsIntegrationUploading } from '~publish/legacy/integrations-bar/state/useIsIntegrationUploading'
import {
  selectCurrentOrganizationId,
  selectShouldShowNBMigration,
} from '~publish/legacy/organizations/selectors'
import { store, useAppSelector } from '~publish/legacy/store'
import { selectIsUploading } from '~publish/legacy/uploads/state/selectors'

import ComposerActionCreators from '../action-creators/ComposerActionCreators'
import { type Draft, DraftMethods } from '../entities/Draft'
import ModalActionCreators from '../shared-components/modal/actionCreators'
import ComposerStore from '../stores/ComposerStore'

const AddPostToThread = styled.button<{
  isDisabled: boolean
}>`
  color: var(--color-text-brand);
  display: flex;
  align-items: center;
  position: relative;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;

  &:hover {
    cursor: pointer;

    ${(props): string | false =>
      !props.isDisabled &&
      `
        svg {
          color: var(--color-text-brand-strong);
          border-color: var(--color-text-brand-strong);
        }

        span {
          color: var(--color-text-brand-strong);
        }
      `}
  }
`

const StartThread = styled.span<{
  isDisabled: boolean
}>`
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  margin-left: 4px;
  color: ${(props): string =>
    props.isDisabled
      ? `var(--color-text-disabled)`
      : `var(--color-text-brand)`};
`

const AddPostToThreadIcon = styled(Add)`
  border-radius: 50%;
  color: ${(props): string =>
    props.isDisabled
      ? `var(--color-text-disabled)`
      : `var(--color-text-brand)`};
  border: ${(props): string =>
    props.isDisabled
      ? `2px solid var(--color-text-disabled)`
      : `2px solid var(--color-text-brand)`};
  height: 21px !important;
  width: 21px !important;
  padding: 3px;
  box-sizing: border-box;
`

const StartThreadWrapper = styled.div`
  position: relative;
  display: flex;
`

const AddThread = ({ draft }: { draft: Draft }): JSX.Element | null => {
  const activeThreadId = ComposerStore.getActiveThreadId()

  const { isIntegrationUploading } = useIsIntegrationUploading()
  const isUploading = useAppSelector((state) =>
    selectIsUploading(state, DraftMethods.getUploaderId(draft)),
  )

  const areUploadsInProgress = isIntegrationUploading || isUploading

  if (!draft.service.supportsThread) {
    return null
  }

  const { hasErrors, errors } = draft.canAddToThread(activeThreadId)

  const addPostToThread = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()

    if (areUploadsInProgress) {
      return
    }

    if (selectShouldShowNBMigration(store.getState())) {
      // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
      ModalActionCreators.openModal('ThreadsMPPaywall')
      return
    }

    const trackingParams = {
      organizationId: selectCurrentOrganizationId(store.getState()),
      clientName: 'publishWeb',
      product: 'publish',
    }
    /**
     * Calling 'Add Thread Clicked' before calling ComposerActionCreators here is important,
     * because after this call the draft should have a Thread already
     */
    if (!draft.hasThread()) {
      BufferTracker.threadsAddThreadClicked(trackingParams)
    }
    ComposerActionCreators.updateDraftAddPostToThread(draft.id)
    BufferTracker.threadsAddTweetClicked(trackingParams)
  }

  const tipNoErrorsContent = `Add ${draft.service.nameOfPost}`
  return (
    <StartThreadWrapper>
      <Tooltip content={!hasErrors ? tipNoErrorsContent : errors.join('\n')}>
        <AddPostToThread
          onClick={addPostToThread}
          isDisabled={hasErrors || areUploadsInProgress}
          data-testid="add-post-to-thread-button"
        >
          <AddPostToThreadIcon isDisabled={hasErrors || areUploadsInProgress} />
          {!draft.hasThread() && (
            <StartThread isDisabled={hasErrors || areUploadsInProgress}>
              Start Thread
            </StartThread>
          )}
        </AddPostToThread>
      </Tooltip>
    </StartThreadWrapper>
  )
}

export default AddThread
